import { useState, useRef } from 'react';
import { Upload, FileSpreadsheet, Download, CheckCircle, XCircle, AlertCircle, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

export default function BulkUploadModal({ teamId, teamName, onUploadComplete }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  const validateAndSetFile = (selectedFile) => {
    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    const validExtensions = ['.csv', '.xlsx', '.xls'];
    
    const extension = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));
    
    if (!validExtensions.includes(extension)) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }
    
    setFile(selectedFile);
    setResults(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      validateAndSetFile(droppedFile);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/players/template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'player_upload_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    
    setUploading(true);
    setResults(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/teams/${teamId}/players/bulk-upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setResults(response.data);
      
      if (response.data.new_players_created > 0 || response.data.existing_players_linked > 0) {
        toast.success(`Successfully added ${response.data.new_players_created + response.data.existing_players_linked} players!`);
        if (onUploadComplete) {
          onUploadComplete();
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Upload failed');
      setResults({
        total_rows: 0,
        new_players_created: 0,
        existing_players_linked: 0,
        errors: [{ row: 0, error: error.response?.data?.detail || 'Upload failed' }],
        players: []
      });
    } finally {
      setUploading(false);
    }
  };

  const resetModal = () => {
    setFile(null);
    setResults(null);
  };

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      setOpen(newOpen);
      if (!newOpen) resetModal();
    }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2" data-testid="bulk-upload-btn">
          <Upload className="w-4 h-4" />
          Bulk Upload
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-teal-600" />
            Bulk Upload Players
          </DialogTitle>
          <DialogDescription>
            Upload players to <span className="font-semibold text-gray-900">{teamName}</span> using CSV or Excel file.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Template Download */}
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100">
            <div className="flex items-center gap-2 text-sm text-blue-700">
              <Download className="w-4 h-4" />
              <span>Need a template?</span>
            </div>
            <Button variant="ghost" size="sm" onClick={downloadTemplate} className="text-blue-600 hover:text-blue-700">
              Download CSV Template
            </Button>
          </div>

          {/* Required Fields Info */}
          <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
            <div className="text-sm text-amber-800">
              <strong>Required columns:</strong> name, phone
              <br />
              <strong>Optional:</strong> email, role (batsman/bowler/all_rounder/wicket_keeper)
            </div>
          </div>

          {/* File Upload Zone */}
          {!results && (
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                dragOver 
                  ? 'border-teal-500 bg-teal-50' 
                  : file 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-gray-300 hover:border-gray-400'
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              
              {file ? (
                <div className="space-y-2">
                  <CheckCircle className="w-10 h-10 mx-auto text-green-500" />
                  <p className="font-medium text-green-700">{file.name}</p>
                  <p className="text-sm text-green-600">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                    }}
                    className="text-gray-500"
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="w-10 h-10 mx-auto text-gray-400" />
                  <p className="font-medium text-gray-700">
                    Drop your file here or click to browse
                  </p>
                  <p className="text-sm text-gray-500">
                    Supports CSV and Excel (.xlsx)
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Upload Button */}
          {file && !results && (
            <Button 
              className="w-full bg-teal-600 hover:bg-teal-700"
              onClick={handleUpload}
              disabled={uploading}
              data-testid="upload-submit-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Players
                </>
              )}
            </Button>
          )}

          {/* Results */}
          {results && (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-3">
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-3 text-center">
                    <div className="text-2xl font-bold text-green-700">
                      {results.new_players_created}
                    </div>
                    <div className="text-xs text-green-600">New Players</div>
                  </CardContent>
                </Card>
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-3 text-center">
                    <div className="text-2xl font-bold text-blue-700">
                      {results.existing_players_linked}
                    </div>
                    <div className="text-xs text-blue-600">Linked Existing</div>
                  </CardContent>
                </Card>
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="p-3 text-center">
                    <div className="text-2xl font-bold text-red-700">
                      {results.errors?.length || 0}
                    </div>
                    <div className="text-xs text-red-600">Errors</div>
                  </CardContent>
                </Card>
              </div>

              {/* Success List */}
              {results.players?.length > 0 && (
                <div className="max-h-40 overflow-y-auto border rounded-lg">
                  <div className="p-2 bg-gray-50 border-b text-xs font-semibold text-gray-600 sticky top-0">
                    Successfully Added Players
                  </div>
                  {results.players.map((player, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 border-b last:border-0 text-sm">
                      {player.status === 'created' ? (
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      )}
                      <span className="flex-1 truncate">{player.name}</span>
                      <span className="text-xs text-gray-500">{player.phone}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        player.status === 'created' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {player.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Error List */}
              {results.errors?.length > 0 && (
                <div className="max-h-40 overflow-y-auto border border-red-200 rounded-lg">
                  <div className="p-2 bg-red-50 border-b border-red-200 text-xs font-semibold text-red-700 sticky top-0">
                    Errors ({results.errors.length})
                  </div>
                  {results.errors.map((error, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-2 border-b border-red-100 last:border-0 text-sm">
                      <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-red-700">Row {error.row}:</span>{' '}
                        <span className="text-gray-700">{error.error}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={resetModal}
                >
                  Upload More
                </Button>
                <Button 
                  className="flex-1 bg-teal-600 hover:bg-teal-700"
                  onClick={() => setOpen(false)}
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
